/**
 * Centralized axios client — the ONLY place HTTP leaves the app (CLAUDE.md F7).
 * Components/hooks call typed service functions that use this client; they never
 * import axios directly.
 *
 * Responsibilities:
 *  - attach the JWT access token (F8) and the active-org header (F1)
 *  - transparently refresh on 401 once, then retry the original request
 *  - on refresh failure, clear tokens and notify the app to log out
 *  - normalize every failure into an {@link ApiError} (F-§2.4) — callers never
 *    see a raw AxiosError, and raw server text is never surfaced to users
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { orgContext, ORG_HEADER } from './orgContext';
import { tokenStorage } from './tokenStorage';
import type { ApiError, TokenPair } from './types';

/** Relative to `VITE_API_URL`. dj-rest-auth + SimpleJWT TokenRefreshView. */
const TOKEN_REFRESH_PATH = '/auth/token/refresh/';

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  // Send cookies so an httpOnly-cookie auth backend works without code changes (F8).
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// Logout notification — let the app react to an unrecoverable 401 without this
// module importing app/router/store code.
// ---------------------------------------------------------------------------
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

// ---------------------------------------------------------------------------
// Request: attach user auth (JWT) + tenant (org header). The JWT authenticates
// the user; the org header selects the active tenant — both are required.
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const access = tokenStorage.getAccess();
  if (access) {
    config.headers.set('Authorization', `Bearer ${access}`);
  }

  const orgId = orgContext.get();
  if (orgId) {
    config.headers.set(ORG_HEADER, orgId);
  }

  // File uploads send `FormData`. The instance default `application/json` would
  // make axios serialize it (losing the file) and omit the multipart boundary —
  // drop it so the browser sets `multipart/form-data; boundary=…` itself.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }

  return config;
});

// ---------------------------------------------------------------------------
// Response: single-flight token refresh on 401, then error normalization.
// ---------------------------------------------------------------------------
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshPromise: Promise<string> | null = null;

/** Refresh the access token using a bare axios call to avoid interceptor recursion. */
async function refreshAccessToken(): Promise<string> {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) throw new Error('No refresh token');

  const { data } = await axios.post<TokenPair>(
    `${baseURL}${TOKEN_REFRESH_PATH}`,
    { refresh },
    { withCredentials: true },
  );
  tokenStorage.set(data.access, data.refresh);
  return data.access;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const isAuthError = error.response?.status === 401;

    if (isAuthError && original && !original._retried && tokenStorage.getRefresh()) {
      original._retried = true;
      try {
        // Coalesce concurrent 401s onto one refresh round-trip.
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
        await refreshPromise;
        return apiClient(original);
      } catch {
        tokenStorage.clear();
        onUnauthorized();
      }
    }

    return Promise.reject(normalizeError(error));
  },
);

// ---------------------------------------------------------------------------
// Error normalization.
// ---------------------------------------------------------------------------
export function normalizeError(error: AxiosError): ApiError {
  // No response → network error, timeout, or cancellation.
  if (!error.response) {
    const isTimeout = error.code === 'ECONNABORTED';
    return {
      status: 0,
      message: isTimeout
        ? 'The request timed out. Please try again.'
        : 'Network error. Check your connection and try again.',
      raw: error.message,
    };
  }

  const { status, data } = error.response;
  const body = data as Record<string, unknown> | undefined;

  // dev13 custom handler (common.exceptions): { detail, code } for APIExceptions.
  const detail = typeof body?.detail === 'string' ? body.detail : undefined;

  const fieldErrors: Record<string, string[]> = {};
  const collect = (key: string, value: unknown) => {
    if (Array.isArray(value)) fieldErrors[key] = value.map(String);
    else if (typeof value === 'string') fieldErrors[key] = [value];
    // Duplicate-style errors arrive as { message, existing_id, ... } (CRM
    // companies/contacts/bids) — keep the message so the field is marked.
    else if (value && typeof value === 'object' && 'message' in value) {
      const msg = (value as { message?: unknown }).message;
      if (typeof msg === 'string') fieldErrors[key] = [msg];
    }
  };

  // dev13 nests serializer field errors under `field_errors`.
  const nested = body?.field_errors;
  if (nested && typeof nested === 'object') {
    for (const [key, value] of Object.entries(nested as Record<string, unknown>)) collect(key, value);
  }
  // `non_field_errors` is returned at the top level.
  if (Array.isArray(body?.non_field_errors)) collect('non_field_errors', body.non_field_errors);

  // Fallback: a plain DRF response ({ field: [...] }) from any endpoint that
  // bypasses the custom handler.
  if (Object.keys(fieldErrors).length === 0 && !detail && body && typeof body === 'object') {
    for (const [key, value] of Object.entries(body)) {
      if (key !== 'code') collect(key, value);
    }
  }

  return {
    status,
    message: detail ?? defaultMessageFor(status),
    fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
    raw: data,
  };
}

function defaultMessageFor(status: number): string {
  if (status === 400 || status === 422) return 'Please check the form for errors.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'The requested item could not be found.';
  if (status >= 500) return 'Something went wrong on our end. Please try again later.';
  return 'Request failed. Please try again.';
}
